import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useProductInfo } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { Container, Flex, Text } from "@mantine/core";
import React, { type JSX, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useAnalytics } from "@/analytics";
import { useVerifiedCustomerStore } from "@/customer-info";
import { FullOfferChecklist } from "@/sales-tools/home-product/components/FullOfferChecklist/FullOfferChecklist";
import { LoadingAnimation } from "@/sales-tools/home-product/components/Icons";
import { InlineSaleTrackerStep } from "@/sales-tools/home-product/components/InlineSaleTracker/InlineSaleTrackerStep";
import { SalesFooter } from "@/sales-tools/home-product/components/StepSpecific/SalesFooter";
import { useLanguageStore } from "@/sales-tools/language-selector/useLanguageStore";
import { usePartner } from "@/sdk";
import { useSessionId } from "@/sdk/sessions/hooks/useSessionId";
import { userCache } from "@/shared-utils";
import type { EligibleProduct } from "../stores";
import { useIntegratedFlowStore } from "../stores/useIntegratedFlowStore";
import { CollectCustomerInfo } from "./CollectCustomerInfo/CollectCustomerInfo";
import { FullChecklistErrorFallback } from "./FullOfferChecklist/components/OfferChecklistFallbacks";
import { SendLink } from "./SendLink/SendLink";
import { FullOfferChecklistStep } from "./StepSpecific/FullOfferChecklistStep";
import { WaitForEnrollment } from "./StepSpecific/WaitForEnrollment";
import type { IntegratedSalesSteps } from "./types";

const logger = getLogger({
    module: "IntegratedFlow",
    supportTeam: "sales",
});

export function IntegratedFlow({ product }: { product: EligibleProduct }): JSX.Element {
    const sessionId = useSessionId();
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const [contactMDN, setContactMDN] = useState(customer?.mdn ?? "");
    const [contactEmail, setContactEmail] = useState(customer?.email ?? "");
    const partner = usePartner();
    const languageState = useLanguageStore();

    const step: IntegratedSalesSteps = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const { dispatcher } = useAnalytics();

    const { productInfo, isLoading } = useProductInfo(
        partner,
        userCache.employeeId,
        [product.productSku],
        languageState.language,
    );

    const primaryProductInfo = productInfo ? productInfo[product.productSku] : undefined;

    useEffect(() => {
        void dispatcher.dispatchBusinessEvent(`${step}_IntegratedFlow`);
        // oxlint-disable-next-line exhaustive-deps
    }, [step]);

    if (isLoading) {
        return (
            <Flex direction="column" h="50%" justify="center" align="center">
                <Text size="sm">Loading product information</Text>
                <Container maw="160px">
                    <LoadingAnimation />
                </Container>
            </Flex>
        );
    }
    if (!primaryProductInfo) {
        logger.error({ product, primaryProductInfo }, "Unable to find primary product info");
        return <Text>An error has occurred while trying to find plan details</Text>;
    }

    const renderStepContent = (salesStep: IntegratedSalesSteps) => {
        switch (salesStep) {
            case "SuggestedOfferStep":
                return (
                    <ErrorBoundary
                        onError={(error) => {
                            logger.error(`Error rendering full offer checklist: ${error}`);
                            void sendErrorMetric("FullOfferChecklistError", { eventSource: "Offer-Checklist" });
                        }}
                        fallbackRender={() => <FullChecklistErrorFallback />}
                    >
                        <FullOfferChecklistStep
                            product={product}
                            salesChecklist={primaryProductInfo?.salesOfferChecklist}
                        />
                    </ErrorBoundary>
                );
            case "CollectCustomerInfoStep":
                return <CollectCustomerInfo />;
            case "WaitingForEnrollmentStep":
                return <WaitForEnrollment product={product} />;
            case "SendLinkStep":
                return (
                    <SendLink
                        product={product}
                        mdn={contactMDN}
                        email={contactEmail}
                        updateMDN={setContactMDN}
                        updateEmail={setContactEmail}
                        sendLinkCommTypeOverride={primaryProductInfo?.sendLinkCommType}
                        helpButtonEnabled={!!primaryProductInfo?.additionalHelpSteps}
                    />
                );
            case "HelpCustomerFindSubscribeButtonStep":
                return (
                    <>
                        {primaryProductInfo && primaryProductInfo.additionalHelpSteps ? (
                            <FullOfferChecklist salesChecklist={primaryProductInfo.additionalHelpSteps} />
                        ) : (
                            <Text>Unable to find locate steps. Please file a support ticket</Text>
                        )}
                        <SalesFooter
                            onBack={() =>
                                useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, "SendLinkStep")
                            }
                            continueButtonProps={{ nextStep: "InlineSaleTrackerStep" }}
                            product={product}
                        />
                    </>
                );
            case "InlineSaleTrackerStep":
                return (
                    <InlineSaleTrackerStep
                        productInfo={primaryProductInfo}
                        product={product}
                        productInfoLink={primaryProductInfo?.productInfoLink}
                    />
                );
            default:
                return null;
        }
    };
    return (
        <Flex h="100%">
            {renderStepContent(primaryProductInfo.salesOfferChecklist ? step : "InlineSaleTrackerStep")}
        </Flex>
    );
}
